import { motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

const EASE = [0.22, 1, 0.36, 1];

/**
 * Scroll-triggered reveal with directional variety. Content is revealed on scroll
 * via an IntersectionObserver, but ALWAYS becomes visible eventually: if the
 * observer is unavailable or never fires (prerender/snapshot tools, anchor-mid-page
 * landings), a fallback timer reveals it anyway so content is never stranded
 * invisible. Reduced motion is handled globally by MotionConfig (reducedMotion="user"),
 * which drops the transform and keeps a fade.
 *
 * `direction` describes how the content travels as it settles:
 *   up    — rises from below (default; matches the original slide-up)
 *   down  — descends from above
 *   left  — glides in from the right
 *   right — glides in from the left
 *   scale — eases up from slightly smaller
 * Explicit `x` / `y` / `scale` props override the preset for that axis.
 */
export function Reveal({
  children,
  delay = 0,
  direction = 'up',
  distance = 20,
  x,
  y,
  scale,
  className,
  as = 'div',
}) {
  const MotionTag = motion[as] ?? motion.div;
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShown(true);
          observer.disconnect();
        }
      },
      { rootMargin: '0px 0px 15% 0px' },
    );
    observer.observe(el);

    // Safety net: reveal even if the element is never scrolled into view (no-scroll
    // contexts like prerender/snapshot tools). Delay is generous so normal readers
    // still get the scroll animation for below-the-fold sections.
    const fallback = setTimeout(() => {
      setShown(true);
      observer.disconnect();
    }, 4000);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  const presets = {
    up: { y: distance },
    down: { y: -distance },
    left: { x: distance },
    right: { x: -distance },
    scale: { scale: 0.94 },
  };
  const preset = presets[direction] ?? presets.up;

  const from = {
    opacity: 0,
    x: x ?? preset.x ?? 0,
    y: y ?? preset.y ?? 0,
    scale: scale ?? preset.scale ?? 1,
  };
  const to = { opacity: 1, x: 0, y: 0, scale: 1 };

  return (
    <MotionTag
      ref={ref}
      className={className}
      initial={from}
      animate={shown ? to : from}
      transition={{ duration: 0.6, delay, ease: EASE }}
    >
      {children}
    </MotionTag>
  );
}
